import React, { useState, useRef, useEffect } from 'react';
import { MessageSquareIcon, SendIcon, Loader2Icon } from 'lucide-react';
import { useStyleConfig } from '../fluidComponents/fluidForm/EmbeddedStyles';
import styled from 'styled-components';
import scrollIntoView from 'scroll-into-view-if-needed';
const MessageBubble = styled.div `
  border-radius: 1rem;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  max-width: 90%;

  color: ${({ $isUser, $styledConfig }) => ($isUser ? $styledConfig?.chatUserBubbleFontColor : $styledConfig?.chatAssistantBubbleFontColor) || '#1f2937'};

  ${({ $hasCustomComponent, $isUser, $styledConfig }) => !$hasCustomComponent && `
    padding: 0.875rem;
    border: 1px solid ${$styledConfig ? ($isUser ? $styledConfig.chatUserBubbleBorderHex : $styledConfig.chatAssistantBubbleBorderHex) : '#e5e7eb'};
    background: ${$styledConfig ? `linear-gradient(
      to bottom right,
      ${$isUser ? $styledConfig.chatUserBubbleColor : $styledConfig.chatAssistantBubbleColor},
      ${$isUser ? $styledConfig.chatUserBubbleGradientEndHex : $styledConfig.chatAssistantBubbleGradientEndHex}
    )` : ($isUser ? '#dbeafe' : '#fed7aa')};
  `}
`;
export const ChatBot = ({ chatPanelTitle, messages = [], onMessage, onCustomAction, isLoading = false, footer, rounded = false, roundedTextArea = true, inputFooterStyle }) => {
    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const styleConfig = useStyleConfig();
    const thinkingMessages = ['Thinking...', 'Processing...', 'Analyzing...', 'Working on it...'];
    const [thinkingIndex, setThinkingIndex] = useState(0);
    useEffect(() => {
        if (!isLoading) {
            setThinkingIndex(0);
            return;
        }
        const interval = setInterval(() => {
            setThinkingIndex(i => (i + 1) % thinkingMessages.length);
        }, 2000);
        return () => clearInterval(interval);
    }, [isLoading]);
    const scrollToBottom = () => {
        scrollIntoView(messagesEndRef.current, { behavior: 'smooth' });
    };
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    useEffect(() => {
        const ta = textareaRef.current;
        if (ta) {
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
        }
    }, [inputValue]);
    const handleSendMessage = () => {
        if (inputValue.trim() && onMessage) {
            onMessage({
                role: 'user',
                content: inputValue.trim()
            });
            setInputValue('');
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    const renderMessage = (message, index) => {
        const hasCustomComponent = !!message.customComponent;
        const isUser = message.role === 'user';
        return (React.createElement("div", { key: index, className: `flex ${isUser ? 'justify-end' : ''} mb-3` },
            React.createElement(MessageBubble, { "$isUser": isUser, "$hasCustomComponent": hasCustomComponent, "$styledConfig": styleConfig },
                message.content && (React.createElement("div", { className: `text-sm ${hasCustomComponent ? 'mb-2' : ''} ${isUser ? 'whitespace-pre-wrap' : ''}`, ...(isUser
                        ? { children: message.content }
                        : { dangerouslySetInnerHTML: { __html: message.content } }) })),
                hasCustomComponent && (React.createElement("div", { className: "mt-2 min-w-[300px] w-full" }, message.customComponent)))));
    };
    return (React.createElement("div", { className: `flex flex-col h-full  ${rounded ? 'rounded-xl' : ''}` },
        chatPanelTitle && (React.createElement("div", { className: `flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white f-border-b ${rounded ? 'rounded-t-xl' : ''}` },
            React.createElement("div", { className: "flex items-center" },
                React.createElement(MessageSquareIcon, { className: "w-4 h-4 text-orange-500 mr-2" }),
                React.createElement("h2", { className: "font-semibold text-gray-800" }, chatPanelTitle)))),
        React.createElement("div", { className: "flex flex-col flex-1 relative overflow-hidden" },
            React.createElement("div", { className: "p-4 flex-1 min-h-0 overflow-auto" },
                messages.map((message, index) => renderMessage(message, index)),
                React.createElement("div", { ref: messagesEndRef })),
            isLoading && (React.createElement("div", { className: "absolute bottom-0 left-0 right-0 p-2 flex justify-center bg-white bg-opacity-80" },
                React.createElement("div", { className: "flex items-center text-orange-500" },
                    React.createElement(Loader2Icon, { className: "w-4 h-4 animate-spin mr-2" }),
                    React.createElement("span", { className: "text-sm" }, thinkingMessages[thinkingIndex]))))),
        React.createElement("div", { style: inputFooterStyle, className: `p-3 ${rounded ? 'rounded-b-xl' : 'rounded-b-lg'}` },
            React.createElement("div", { className: "flex bg-white flex-col p-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm" },
                React.createElement("div", { className: "flex items-end full-width mb-1" },
                    React.createElement("textarea", { ref: textareaRef, rows: 1, value: inputValue, onChange: (e) => setInputValue(e.target.value), onKeyDown: handleKeyDown, placeholder: "Type your message...", className: "grow outline-none resize-none overflow-y-auto", style: { minHeight: 25, maxHeight: 120 }, readOnly: isLoading }),
                    React.createElement("button", { onClick: handleSendMessage, disabled: isLoading || !inputValue.trim(), className: "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-3 py-2.5 rounded-xl transition-all duration-300 flex items-center shadow-md shrink-0" },
                        React.createElement(SendIcon, { className: "w-4 h-4" }))),
                footer))));
};
